// © Microsoft Corporation. All rights reserved.

/**
 * Interface to define data needed for AutoSuggest initialization
 */
export interface AutoSuggestInitProperties {
    /** AutoSuggest properties */
    autoSuggestProperties: AutoSuggestProperties;

    /** Flag to disable autosuggest */
    disableAutoSuggest?: boolean;

    /** Request id */
    requestId: string;

    /** Flag to indicate if dark mode is on */
    isDarkMode: boolean;

    /** Handler for starting autosuggest perf marker */
    startPerfMarkerHandler?: (perfMarkerId: string) => void;

    /** Handler for ending autosuggest perf marker */
    endPerfMarkerHandler?: (perfMarkerId: string) => void;

    /** Search box context, sets the value for "this" for different handlers */
    searchBoxContext: any;

    /** Flag to open search in new window */
    searchOpenInNewWindow?: boolean;

    /** Telemetry callback */
    telemetryCallback: (element: HTMLElement, action?: string, behavior?: number) => void;

    /** Search action url */
    action: string;
}

/**
 * Interface to define AutoSuggest properties
 */
export interface AutoSuggestProperties {
    /** Zero Input Item Count */
    zeroInputItemCount?: number;

    /** Disable Search History */
    disableSearchHistory?: boolean;

    /** Max search returned items */
    maxItemCount?: number;

    /** Cookie sharing delay */
    cookieSharingDelayInSeconds: number;

    /** Delay binding value */
    delayBind: AutoSuggestDelayBinding;

    /** Disable Suggestion fetching when no input is provided */
    disableSuggestionFetchOnEmpty?: boolean;

    /** Enable auto suggest features */
    enableFeatures?: boolean;

    /** Enable business search results */
    businessSearchResultsEnabled?: boolean;

    /** Enable feedswitcher check for business search */
    feedSwitcherCheckEnabled?: boolean;

    /** Enable new SnR url */
    enableNewSnrUrl?: boolean;

    /** AutoSuggest features */
    features?: string;

    /** OS market value */
    osMarket?: string;

    /** OS language value */
    osLanguage?: string;

    /** Partner endpoint value */
    partnerEndpoint?: string;

    /** Use market based AutoSuggest url */
    useMarketBasedUrl?: boolean;
}

/**
 * Enum that defines autosuggest delay binding options
 */
export enum AutoSuggestDelayBinding {
    /** Download immediately */
    Immediately = 0,

    /** Download on page load */
    OnPageLoad = 1,

    /** Download on user interaction */
    OnUserInteraction = 2,

    /**
     * Applicable for enterprise users only
     * Initialization is done on page load for enterprise users
     * If Business search results feature is disabled then this binding behaves like OnUserInteraction.
     */
    OnBusinessSearchResultsReady = 3
}

/**
 * Perf marker id for AutoSuggest
 */
export const autoSuggestPerfMarker = "Search-AutoSuggest";

/**
 * ID for the html element for the search box to attach autosuggest to
 */
export const searchBoxId = "srchfrm";

/**
 * ID for the html input element for the search box
 */
export const searchInputId = "q";
